import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Table, Button, Modal, Typography, Card, Row, Col, Input, Tag, Space, Tooltip } from 'antd';
import {
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  UndoOutlined
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import moment from 'moment';
import CompanyRegister from '../../components/auth/CompanyRegister';
import RejectReasonModal from '../../components/admin/RejectReasonModal';

const { Title, Text } = Typography;

const CompanyManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCompany, setEditingCompany] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  // Fetch companies from API
  const loadCompanies = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/companies');
      if (response.data && response.data.success) {
        setCompanies(response.data.data);
        setFilteredCompanies(response.data.data);
      } else {
        throw new Error(response.data?.message || 'Failed to fetch companies');
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      toast.error('Error loading companies. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  // Search Logic
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    const filtered = companies.filter((company) => 
      company.companyName?.toLowerCase().includes(value) ||
      company.email?.toLowerCase().includes(value) ||
      company.mobile?.includes(value)
    );
    setFilteredCompanies(filtered);
  };

  const handleReset = () => {
    setSearchText('');
    setFilteredCompanies(companies);
  };

  // Modal handlers
  const openAddModal = () => {
    setEditingCompany(null);
    setIsModalOpen(true);
  };

  const openEditModal = (company) => {
    setEditingCompany(company);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setEditingCompany(null), 300);
  };

  const handleCompanySuccess = () => {
    closeModal();
    loadCompanies();
  };

  const handleDelete = async (id) => {
    const company = companies.find((c) => c._id === id);
    if (!company) return;
    if (!window.confirm(`Are you sure you want to delete "${company.companyName}"?`)) return;

    try {
      const response = await axios.delete(`/api/companies/${id}`);
      if (response.data && response.data.success) {
        toast.success('Company deleted successfully!');
        loadCompanies();
      } else {
        throw new Error(response.data?.message || 'Failed to delete company');
      }
    } catch (error) {
      toast.error('Failed to delete company.');
    }
  };

  // Approve / Reject / Revert registration status
  const handleApprove = (companyId) => {
    Modal.confirm({
      title: 'Approve Company Registration',
      content: 'Are you sure you want to approve this company?',
      okText: 'Approve',
      okType: 'primary',
      onOk: async () => {
        setActionLoading(true);
        try {
          const response = await axios.put(`/api/admin/companies/${companyId}/approve`, {}, authHeaders());
          if (response.data && response.data.success) {
            toast.success('Company approved successfully');
            loadCompanies();
          }
        } catch (error) {
          toast.error('Failed to approve company');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleRevert = (companyId) => {
    Modal.confirm({
      title: 'Revert to Pending',
      content: 'This will move the record back to Pending review and it will be hidden from the public site if currently approved. Continue?',
      okText: 'Revert',
      onOk: async () => {
        setActionLoading(true);
        try {
          const response = await axios.put(`/api/admin/companies/${companyId}/revert`, {}, authHeaders());
          if (response.data && response.data.success) {
            toast.success('Company reverted to pending');
            loadCompanies();
          }
        } catch (error) {
          toast.error('Failed to revert company');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleReject = (company) => {
    setRejectTarget(company);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const submitRejection = async () => {
    if (!rejectReason.trim()) {
      toast.warning('Please provide a reason for rejection');
      return;
    }
    setActionLoading(true);
    try {
      const response = await axios.put(
        `/api/admin/companies/${rejectTarget._id}/reject`,
        { reason: rejectReason },
        authHeaders()
      );
      if (response.data && response.data.success) {
        toast.success('Company rejected');
        setRejectModalOpen(false);
        loadCompanies();
      }
    } catch (error) {
      toast.error('Failed to reject company');
    } finally {
      setActionLoading(false);
    }
  };

  // Statistics Calculation
  const stats = {
    total: companies.length,
    active: companies.filter(c => c.status === 'active').length,
    pending: companies.filter(c => c.status === 'pending').length,
    rejected: companies.filter(c => c.status === 'rejected').length // Assuming you might have this status
  };

  // Styles based on the screenshot
  const cardStyles = {
    total: { bg: '#e6f7ff', color: '#1890ff', icon: <UserOutlined /> },
    approved: { bg: '#f6ffed', color: '#52c41a', icon: <CheckCircleOutlined /> },
    pending: { bg: '#f9f0ff', color: '#722ed1', icon: <ClockCircleOutlined /> },
    rejected: { bg: '#fff1f0', color: '#f5222d', icon: <CloseCircleOutlined /> },
  };

  const StatCard = ({ title, count, type }) => (
    <Card bordered={false} style={{ backgroundColor: cardStyles[type].bg, borderRadius: '8px', textAlign: 'center' }}>
      <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>{title}</Text>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', fontSize: '24px', color: cardStyles[type].color, fontWeight: 'bold' }}>
        {cardStyles[type].icon}
        <span>{count}</span>
      </div>
    </Card>
  );

  const columns = [
    {
      title:<span style={{ fontWeight: 500 }}>Sr. No.</span>,
      key: 'index',
      width: 70,
      render: (text, record, index) => <Text style={{ color: '#666', fontSize:"16px" }}>{index + 1}</Text>,
    },
    {
      title: <span style={{ fontWeight: 500 }}>Company Name</span>,
      dataIndex: 'companyName',
      key: 'companyName',
      render: (text) => <Text strong style={{ color: '#333' , fontSize:"15px" }}>{text}</Text>,
    },
    {
      title: <span style={{ fontWeight: 500 }}>Email / Contact</span>,
      dataIndex: 'email',
      key: 'email',
      render: (text) => <Text style={{ color: '#555',fontSize:"16px" }}>{text}</Text>,
    },
    {
      title: <span style={{ fontWeight: 500 }}>Website / Doc</span>,
      dataIndex: 'website',
      key: 'website',
      render: (website) => (
        website ? (
          <a href={website} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '5px' ,fontSize:"16px"}}>
            <EyeOutlined /> View
          </a>
        ) : <Text type="secondary">-</Text>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Status</span>,
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = status === 'approved' ? 'green' : status === 'pending' ? 'gold' : 'red';
        return (
          <Tag color={color} style={{ px: 4, py: 2, borderRadius: '4px', textTransform: 'capitalize' ,fontSize:"16px"}}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: <span style={{ fontWeight: 500 }}>Action</span>,
      key: 'actions',
      width: 260,
      render: (_, record) => (
        <Space wrap>
          {record.status === 'pending' && (
            <>
              <Tooltip title="Approve">
                <Button
                  size="small"
                  type="link"
                  icon={<CheckCircleOutlined />}
                  style={{ color: '#52c41a' }}
                  onClick={() => handleApprove(record._id)}
                />
              </Tooltip>
              <Tooltip title="Reject">
                <Button
                  size="small"
                  type="link"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => handleReject(record)}
                />
              </Tooltip>
            </>
          )}
          {(record.status === 'approved' || record.status === 'rejected') && (
            <Tooltip title="Revert to Pending">
              <Button
                size="small"
                type="link"
                icon={<UndoOutlined />}
                onClick={() => handleRevert(record._id)}
              />
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <Button
              size="small"
              type="link"
              icon={<EditOutlined />}
              style={{ color: '#470172' }}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              size="small"
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record._id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      
      {/* 1. Statistics Section */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm">
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} md={6}>
            <StatCard title="Total Requests" count={stats.total} type="total" />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard title="Approved Requests" count={stats.active} type="approved" />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard title="Pending Requests" count={stats.pending} type="pending" />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard title="Rejected Requests" count={stats.rejected} type="rejected" />
          </Col>
        </Row>
      </div>

      {/* 2. Main Content Area */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        
        {/* Filter Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <div className="flex items-center gap-2 w-full md:w-auto">
            <Input 
              prefix={<SearchOutlined className="text-gray-400" />}
              placeholder="Search by name, email..." 
              value={searchText}
              onChange={handleSearch}
              style={{ width: 300, borderRadius: '4px' }}
            />
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Reset
            </Button>
          </div>
          
          <div className="flex items-center gap-4">
            <Text type="secondary" className="hidden md:block">
              Showing {filteredCompanies.length} of {companies.length} records
            </Text>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={openAddModal}
              style={{ backgroundColor: '#1890ff', borderRadius: '4px' }}
            >
              Add Company
            </Button>
          </div>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredCompanies}
          loading={loading}
          rowKey={(record) => record._id}
          scroll={{ x: 'max-content' }}
          pagination={{
            pageSize: 10,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
          }}
          rowClassName="align-middle"
        />
      </div>

      {/* Add/Edit Modal */}
      <Modal
        title={editingCompany ? 'Edit Company' : 'Add New Company'}
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        width={800}
        centered
        destroyOnClose={true}
      >
        <CompanyRegister
          onSuccess={handleCompanySuccess}
          editingCompany={editingCompany}
          isAdminEdit={true}
        />
      </Modal>

      <RejectReasonModal
        open={rejectModalOpen}
        title="Reject Company Registration"
        entityLabel={rejectTarget?.companyName}
        reason={rejectReason}
        onReasonChange={setRejectReason}
        onOk={submitRejection}
        onCancel={() => setRejectModalOpen(false)}
        confirmLoading={actionLoading}
      />
    </div>
  );
};

export default CompanyManagement;